/**
 * Acquisition params forwarded by LP CTAs and ad platforms (ENG-503).
 *
 * The allowlist mirrors landing-pages' withAcquisitionParams(): UTM set,
 * ad click IDs, PartnerStack's gspk referral key, and the lp=<pathname>
 * marker identifying the source landing page. Harvested from the arrival
 * URL and registered as PostHog super-properties — they persist
 * last-touch (until a later campaign arrival overwrites them), not just
 * for the session.
 *
 * gspk is registered here specifically so the Payments-channel Slack
 * destination's Partner classification can read it as a synchronous event
 * property instead of waiting on the async person-properties merge that
 * computes person.$initial_current_url — the same race class fixed for
 * gclid/fbclid/utm_source (see the destination's description in PostHog).
 */
export const ACQUISITION_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "fbclid",
  "gclid",
  "gbraid",
  "wbraid",
  "gad_campaignid",
  "msclkid",
  "ttclid",
  "gspk",
  "rwr",
  "_kx",
  "lp",
] as const;

export function harvestAcquisitionParams(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const harvested: Record<string, string> = {};
  ACQUISITION_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) harvested[key] = value;
  });
  return harvested;
}

/**
 * Hostnames that mean "this referrer is us, not an external channel" — a
 * page on our own domain referring here carries no attribution signal.
 * Mirrors the WP-side list in consultation-redirect.js's
 * INTERNAL_REFERRER_HOSTNAMES — keep the two in sync.
 */
const INTERNAL_REFERRER_HOSTNAMES = new Set([
  "runwayhealth.com",
  "www.runwayhealth.com",
  "app.runwayhealth.com",
  "staging.runwayhealth.com",
  "dev.runwayhealth.com",
]);

const REFERRING_DOMAIN_PARAM = "ref_domain";

/**
 * Resolves this arrival's external referring domain.
 *
 * Prefers the WP-forwarded `ref_domain` param when present — the true
 * first-hop referrer. By the time the app loads on a WP -> app handoff,
 * `document.referrer` only says "runwayhealth.com" (the internal hop), not
 * the original external source, so the app cannot recover it on its own for
 * that path; WP captures and forwards it instead (see
 * `withReferringDomain` in consultation-redirect.js). Falls back to this
 * page's own `document.referrer` when it is itself external — a direct or
 * LP arrival with no WP hop in between.
 *
 * The forwarded value passes the same internal-hostname check as the
 * referrer fallback: WP filters internal referrers before forwarding, so a
 * `ref_domain` naming our own domain only occurs on a crafted or shared
 * URL, and registering it would let downstream classification read an
 * internal hop as a confident attribution claim.
 */
export function harvestReferringDomain(
  search: string,
  referrer: string
): string | undefined {
  const forwarded = new URLSearchParams(search).get(REFERRING_DOMAIN_PARAM);
  if (forwarded && !INTERNAL_REFERRER_HOSTNAMES.has(forwarded)) {
    return forwarded;
  }

  if (!referrer) return undefined;

  let hostname: string;
  try {
    hostname = new URL(referrer).hostname;
  } catch {
    return undefined;
  }

  return INTERNAL_REFERRER_HOSTNAMES.has(hostname) ? undefined : hostname;
}

/**
 * Registers the resolved referring domain as a synchronous PostHog
 * super-property, so the Payments-channel Slack destination's Organic
 * Search / Direct / referral-domain classification can read
 * `event.properties.referring_domain` instead of racing PostHog's async
 * `$initial_referring_domain` merge — the same race class fixed for
 * gclid/fbclid/utm_source/gspk.
 *
 * Deliberately NOT gated the way `applyAcquisitionParams` is: an arrival
 * with no resolvable external referrer (organic-with-no-referrer, or an
 * internal WP hop with nothing forwarded) is a no-op, preserving whatever
 * was registered on an earlier, real external touch — last-touch semantics
 * for the field that is actually present, mirroring the chimera prevention
 * already used for campaign params.
 */
export function applyReferringDomain(
  ph: AcquisitionRegistrar,
  referringDomain: string | undefined
): void {
  if (!referringDomain) return;
  ph.unregister("referring_domain");
  ph.register({ referring_domain: referringDomain });
}

export interface AcquisitionRegistrar {
  register: (props: Record<string, string>) => void;
  unregister: (key: string) => void;
}

/**
 * Registers a harvested acquisition-param set as PostHog super-properties,
 * replacing any previously registered set rather than merging into it
 * (ENG-506). PostHog's register() merges per-key, so a later arrival that
 * only carries some allowlisted params (e.g. just `lp`, no click ID) would
 * otherwise leave stale fields from an earlier, different campaign in
 * place — a "chimera" of two unrelated arrivals. An organic arrival
 * (empty acquisitionProps) is a no-op, preserving last-touch semantics:
 * the prior registration stands until a later *qualifying* arrival
 * replaces it.
 */
export function applyAcquisitionParams(
  ph: AcquisitionRegistrar,
  acquisitionProps: Record<string, string>
): void {
  if (Object.keys(acquisitionProps).length === 0) return;
  ACQUISITION_PARAMS.forEach((key) => ph.unregister(key));
  ph.register(acquisitionProps);
}

/**
 * Reads the PostHog distinct_id an LP forwarded on CTA handoff (ENG-508).
 *
 * Cross-subdomain person merge used to rely on a `.runwayhealth.com`-scoped
 * PostHog cookie (ENG-503), but that cookie is sent to every subdomain
 * including the API, where its content can trip WAF rules. Forwarding the
 * id via URL param and adopting it through PostHog's `bootstrap.distinctID`
 * achieves the same merge without ever putting PostHog data in a header
 * that reaches the API.
 */
export function readInboundDistinctId(search: string): string | undefined {
  const params = new URLSearchParams(search);
  const value = params.get("ph_distinct_id");
  return value ? value : undefined;
}

/**
 * Reads the PostHog $session_id an LP forwarded on CTA handoff (ENG-735).
 *
 * ENG-508 merged cross-domain *person* identity but left session continuity
 * untouched: PostHog's $session_id is domain-scoped, so a new session starts
 * the moment the browser lands on the app, even for a visitor whose identity
 * was just merged. Forwarding the LP's session id and adopting it via
 * `bootstrap.sessionID` closes that gap the same way `ph_distinct_id` closes
 * the identity one.
 */
export function readInboundSessionId(search: string): string | undefined {
  const params = new URLSearchParams(search);
  const value = params.get("ph_session_id");
  return value ? value : undefined;
}

/**
 * The localStorage key posthog-js persists identity under. Mirrors the
 * library's own derivation (`Persistence.name`): `ph_<token>_posthog`, with
 * the token's base64-unsafe characters escaped.
 */
export function posthogPersistenceKey(apiKey: string): string {
  const safeToken = apiKey
    .replace(/\+/g, "PL")
    .replace(/\//g, "SL")
    .replace(/=/g, "EQ");
  return `ph_${safeToken}_posthog`;
}

/**
 * True when this browser already holds a PostHog identity.
 *
 * Guards `bootstrap.distinctID`, which is NOT safe to pass unconditionally.
 * In posthog-js 1.235.2 the bootstrap branch runs
 * `register({ distinct_id, $device_id })` and forces USER_STATE to
 * "anonymous" with no check for an existing stored id — so handing it an
 * inbound id would overwrite a returning visitor's distinct_id, downgrade an
 * identified user to anonymous, and orphan the person they already had.
 *
 * Adoption is therefore limited to browsers with no PostHog identity yet —
 * the genuinely new visitor, which is the case the ad-click handoff exists
 * to merge. A returning visitor keeps the identity they already have; their
 * marketing-side person stays separate for now (see the alias follow-up in
 * the PR) rather than being destroyed.
 */
export function hasStoredPostHogIdentity(
  apiKey: string,
  storage: Pick<Storage, "getItem"> | undefined
): boolean {
  if (!storage) return false;

  let raw: string | null;
  try {
    raw = storage.getItem(posthogPersistenceKey(apiKey));
  } catch {
    // Storage can throw outright (Safari private mode, blocked cookies).
    // Treat an unreadable store as "no identity we can safely overwrite".
    return false;
  }

  if (!raw) return false;

  try {
    const parsed = JSON.parse(raw) as { distinct_id?: unknown };
    return typeof parsed.distinct_id === "string" && parsed.distinct_id !== "";
  } catch {
    return false;
  }
}

/**
 * posthog-js persists the current session under the `$sesid` key in the
 * same persistence blob as distinct_id: `[lastActivityTimestamp, sessionId,
 * sessionStartTimestamp]` (see posthog-js's `SessionIdManager`/`SESSION_ID`
 * constant). Mirrors the library's own idle-timeout default (30 minutes)
 * plus its own 1.1x buffer, so this guard's notion of "still active" agrees
 * with the library's.
 */
const SESSION_IDLE_TIMEOUT_MS = 30 * 60 * 1000 * 1.1;

/**
 * True when this browser already has a live (non-idle) PostHog session.
 *
 * Guards `bootstrap.sessionID`, which is even less safe to pass
 * unconditionally than `bootstrap.distinctID`: posthog-js's session-bootstrap
 * path (unlike its distinct_id path) has NO existing-session check at all —
 * it unconditionally overwrites whatever session is already live. Passing an
 * inbound session id to a browser with its own in-progress session wouldn't
 * just relabel a person (the identity-guard failure mode) — it would splice
 * the LP's session onto a returning visitor's live app session mid-use,
 * truncating what they were already doing.
 *
 * Adoption is therefore limited to browsers with no active session — the
 * genuinely new arrival the CTA handoff exists to merge.
 */
export function hasActiveSession(
  apiKey: string,
  storage: Pick<Storage, "getItem"> | undefined
): boolean {
  if (!storage) return false;

  let raw: string | null;
  try {
    raw = storage.getItem(posthogPersistenceKey(apiKey));
  } catch {
    return false;
  }

  if (!raw) return false;

  try {
    const parsed = JSON.parse(raw) as { $sesid?: unknown };
    const sesid = parsed.$sesid;
    if (!Array.isArray(sesid) || typeof sesid[0] !== "number") return false;

    const lastActivityTimestamp = sesid[0];
    return Date.now() - lastActivityTimestamp < SESSION_IDLE_TIMEOUT_MS;
  } catch {
    return false;
  }
}
